/**
 * Day 12 — Activation Command Center
 * ────────────────────────────────────────────────────────────────────
 * Admin-only single-pane operational readiness dashboard.
 *
 * Consumes ONLY `/api/platform/readiness` (read-only aggregator).
 * There are NO mutation buttons on this page. The flag flips and rollout-state
 * advancements are still performed via the existing `/api/org/settings`
 * endpoint or the staircase scripts — this page is the **pre-flight check**.
 *
 * Permission required: `allocation.live_split_billing.view`
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { SplitBillingDriftTile } from '@/components/admin/SplitBillingDriftTile';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Shield,
  ShieldAlert,
  Activity,
  RefreshCcw,
  Lock,
  Database,
  FileCheck2,
  Layers,
  ListChecks,
} from 'lucide-react';

const verdictPalette = {
  GREEN: { bg: 'bg-emerald-50', text: 'text-emerald-900', ring: 'ring-emerald-200', icon: <CheckCircle2 className="h-6 w-6 text-emerald-600" />, label: 'GREEN — Safe to proceed' },
  YELLOW: { bg: 'bg-amber-50', text: 'text-amber-900', ring: 'ring-amber-300', icon: <AlertTriangle className="h-6 w-6 text-amber-600" />, label: 'YELLOW — Caution / under controlled rollout' },
  RED: { bg: 'bg-red-50', text: 'text-red-900', ring: 'ring-red-300', icon: <XCircle className="h-6 w-6 text-red-600" />, label: 'RED — Activation blocked / mutations at risk' },
  UNKNOWN: { bg: 'bg-slate-50', text: 'text-slate-900', ring: 'ring-slate-200', icon: <Activity className="h-6 w-6 text-slate-600" />, label: 'UNKNOWN — endpoint unreachable' },
};

function Pill({ label, value, ok }) {
  const isOk = typeof ok === 'boolean' ? ok : value === true || value === 'OK';
  return (
    <div className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${isOk ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid={`pill-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <span className="text-slate-700">{label}</span>
      <Badge variant={isOk ? 'default' : 'secondary'} className={isOk ? 'bg-emerald-600' : 'bg-slate-500'}>
        {String(value)}
      </Badge>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <Card className="border border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">{children}</CardContent>
    </Card>
  );
}

export default function ActivationCommandCenterPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/platform/readiness');
      setData(res?.data?.data || res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load readiness');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [load]);

  const verdict = data?.verdict || 'UNKNOWN';
  const palette = verdictPalette[verdict] || verdictPalette.UNKNOWN;
  const flags = data?.flags || {};
  const rollout = data?.rollout_state || {};
  const journal = data?.journal_counts || {};
  const finance = data?.finance_collections || {};
  const audit = data?.audit_health || {};
  const gates = data?.gate_baselines || {};
  const killSwitch = data?.kill_switch || {};
  const next_step = data?.next_step || {};
  const phase3 = data?.phase3_status || {};
  const allowlist = data?.allowlist_posture || {};
  const governance = data?.governance_posture || {};
  const recovery = data?.recovery_posture || {};

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6" data-testid="activation-command-center">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Activation Command Center</h1>
          <p className="mt-1 text-sm text-slate-600">
            Pre-flight readiness aggregator for Phase 3 controlled rollout. Visibility only — no mutation controls on this page.
          </p>
        </div>
        <Button onClick={load} disabled={loading} data-testid="refresh-readiness-btn">
          <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4 text-sm text-red-900" data-testid="readiness-error">
            Failed to load readiness: {error}
          </CardContent>
        </Card>
      )}

      {/* Verdict banner */}
      <Card className={`border ${palette.ring} ${palette.bg}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-white p-3 shadow-sm">{palette.icon}</div>
            <div className="flex-1">
              <div className={`text-xs font-semibold uppercase tracking-wide ${palette.text}`}>Overall Verdict</div>
              <div className={`text-2xl font-bold ${palette.text}`} data-testid="readiness-verdict">{palette.label}</div>
              <div className="mt-1 text-sm text-slate-700">
                Phase 3 status: <span className="font-semibold">{phase3.status || '—'}</span>
                {' · '}Risk: <span className="font-semibold">{phase3.risk_level || '—'}</span>
                {' · '}Generated: {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Mutations Possible</div>
              <Badge className={data?.mutations_possible ? 'bg-red-600 text-white' : 'bg-slate-600 text-white'} data-testid="mutations-possible-badge">
                {data?.mutations_possible ? 'YES — LIVE' : 'NO — gated'}
              </Badge>
            </div>
          </div>

          {Array.isArray(data?.blockers) && data.blockers.length > 0 && (
            <div className="mt-4 rounded-md border border-red-300 bg-white p-3" data-testid="readiness-blockers">
              <div className="text-sm font-semibold text-red-800">Active blockers</div>
              <ul className="mt-1 list-disc pl-5 text-sm text-red-700">
                {data.blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(data?.notes) && data.notes.length > 0 && (
            <div className="mt-4 rounded-md border border-amber-300 bg-white p-3" data-testid="readiness-notes">
              <div className="text-sm font-semibold text-amber-800">Notes</div>
              <ul className="mt-1 list-disc pl-5 text-sm text-amber-700">
                {data.notes.map((n) => <li key={n}>{n}</li>)}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Section title="Compensation Recovery (Day 15 — Pre-Trigger)" icon={<RefreshCcw className="h-4 w-4 text-rose-600" />}>
          <div
            className={`rounded-md border px-3 py-2 text-sm ${recovery.verdict === 'SAFE' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : recovery.verdict === 'AT_RISK' ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
            data-testid="recovery-posture-summary"
          >
            <div className="font-semibold">
              Compensation sweep dry-run verdict: {recovery.verdict || 'UNKNOWN'}
            </div>
            <div className="mt-1 text-xs">{recovery.note || '—'}</div>
          </div>
          <Pill label="Plan entries scanned" value={recovery.plan_count ?? 0} ok={(recovery.plan_count ?? 0) >= 0} />
          <Pill label="Critical actions" value={recovery.critical_count ?? 0} ok={(recovery.critical_count ?? 0) === 0} />
          <Pill label="Warning actions" value={recovery.warn_count ?? 0} ok={(recovery.warn_count ?? 0) === 0} />
          <Pill label="Orphan IN_FLIGHT" value={recovery.drift?.orphan_in_flight ?? 0} ok={(recovery.drift?.orphan_in_flight ?? 0) === 0} />
          <Pill label="COMPENSATION_FAILED" value={recovery.drift?.compensation_failed ?? 0} ok={(recovery.drift?.compensation_failed ?? 0) === 0} />
          <Pill label="Unknown status" value={recovery.drift?.unknown_status ?? 0} ok={(recovery.drift?.unknown_status ?? 0) === 0} />
          {Array.isArray(recovery.blockers) && recovery.blockers.length > 0 && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900" data-testid="recovery-blockers">
              <div className="font-semibold">Recovery blockers</div>
              <ul className="mt-1 list-disc pl-4">
                {recovery.blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}
        </Section>

        <Section title="STAGE_2 Governance (Day 14 — Step 3)" icon={<ShieldAlert className="h-4 w-4 text-fuchsia-600" />}>
          <div
            className={`rounded-md border px-3 py-2 text-sm ${governance.stage_2_active ? 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
            data-testid="governance-posture-summary"
          >
            <div className="font-semibold">
              {governance.stage_2_active
                ? `STAGE_2 ACTIVE — ${governance.mode || 'OFF'}`
                : 'STAGE_2 INACTIVE — rollout stage = OFF'}
            </div>
            <div className="mt-1 text-xs">
              Trigger flag: {governance.trigger_armed ? 'ARMED (live mutations possible)' : 'OFF (refusal preserved)'}
            </div>
          </div>
          <Pill label="Stage 2 active" value={governance.stage_2_active} ok={governance.stage_2_active === true || governance.stage_2_active === false} />
          <Pill label="Dual-control satisfied" value={governance.dual_control_satisfied} ok={governance.dual_control_satisfied === true} />
          <Pill label="Approver" value={governance.approver_id || '—'} ok />
          <Pill label="Activation actor" value={governance.activation_actor || '—'} ok />
          <Pill label="Stage transitions" value={governance.stage_history_count ?? 0} ok />
          {governance.rollback_actor && (
            <Pill label="Last rollback actor" value={governance.rollback_actor} ok />
          )}
          {Array.isArray(governance.warnings) && governance.warnings.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="governance-warnings">
              <div className="font-semibold">Governance warnings</div>
              <ul className="mt-1 list-disc pl-4">
                {governance.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {governance.last_stage_transition && (
            <div className="rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700" data-testid="last-stage-transition">
              <div className="font-semibold">Last transition</div>
              <div>
                {governance.last_stage_transition.action}: {governance.last_stage_transition.from_stage} → {governance.last_stage_transition.to_stage}
              </div>
              <div className="text-slate-500">
                {governance.last_stage_transition.at ? new Date(governance.last_stage_transition.at).toLocaleString() : ''}
              </div>
            </div>
          )}
        </Section>

        <Section title="Allowlist Posture (Day 13 — Step 2)" icon={<Layers className="h-4 w-4 text-sky-600" />}>
          <div
            className={`rounded-md border px-3 py-2 text-sm ${allowlist.enabled ? 'border-sky-300 bg-sky-50 text-sky-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
            data-testid="allowlist-posture-summary"
          >
            <div className="font-semibold">{allowlist.summary || '—'}</div>
            {allowlist.rollout_warning && (
              <div className="mt-1 text-xs text-amber-800" data-testid="allowlist-rollout-warning">
                {allowlist.rollout_warning}
              </div>
            )}
          </div>
          <Pill label="Allowlist flag" value={allowlist.enabled} ok={allowlist.enabled === true} />
          <Pill label="Rollout mode" value={allowlist.mode || 'OFF'} ok={allowlist.mode === 'OFF'} />
          <Pill label="Rollout stage" value={allowlist.rollout_stage || 'OFF'} ok={allowlist.rollout_stage === 'OFF'} />
          <Pill label="Allowlisted shifts" value={allowlist.size ?? 0} ok={(allowlist.size ?? 0) === 0} />
          <Pill label="Shift opt-in eligible" value={allowlist.eligible_for_optin} ok={allowlist.eligible_for_optin === false} />
        </Section>

        <Section title="Kill-Switch" icon={<Lock className="h-4 w-4 text-slate-600" />}>
          <Pill label="Scaffold guard enabled" value={killSwitch.scaffold_guard_enabled} ok={killSwitch.scaffold_guard_enabled === true} />
          <Pill label="Mutation endpoints active" value={killSwitch.mutation_endpoints_active} ok={killSwitch.mutation_endpoints_active === false} />
          <div className="text-xs text-slate-500" data-testid="kill-switch-note">{killSwitch.note}</div>
        </Section>

        <Section title="Phase 3 Feature Flags" icon={<Shield className="h-4 w-4 text-slate-600" />}>
          {Object.entries(flags).map(([k, v]) => (
            <Pill key={k} label={k.replace(/_/g, ' ')} value={v} ok={v === false} />
          ))}
        </Section>

        <Section title="Rollout State" icon={<Layers className="h-4 w-4 text-slate-600" />}>
          <Pill label="Tenant" value={rollout.org_id || '—'} ok />
          <Pill label="Stage" value={rollout.phase3_stage || 'OFF'} ok={rollout.phase3_stage === 'OFF'} />
          <Pill label="Mode" value={rollout.mode || 'OFF'} ok={rollout.mode === 'OFF'} />
          <Pill label="Allowlist size" value={rollout.allowlist_size ?? 0} ok={(rollout.allowlist_size ?? 0) === 0} />
        </Section>

        <Section title="Journal Lifecycle" icon={<ListChecks className="h-4 w-4 text-slate-600" />}>
          {Object.entries(journal).map(([k, v]) => (
            <Pill key={k} label={k} value={v} ok={v === 0} />
          ))}
        </Section>

        <Section title="Reconciliation" icon={<FileCheck2 className="h-4 w-4 text-slate-600" />}>
          <Pill label="Enforced" value={data?.reconciliation?.enforced} ok={data?.reconciliation?.enforced === false} />
          <div className="text-xs text-slate-500">{data?.reconciliation?.note}</div>
        </Section>

        <Section title="Audit Health (24h)" icon={<Activity className="h-4 w-4 text-slate-600" />}>
          <Pill label="audit_logs emitted" value={audit.audit_logs_last_24h ?? '—'} ok={(audit.audit_logs_last_24h ?? 0) > 0} />
          <Pill label="refusal_log emitted" value={audit.refusal_log_last_24h ?? '—'} ok={(audit.refusal_log_last_24h ?? 0) >= 0} />
          <Pill label="bulk sentinel residue" value={audit.bulk_sentinel_residue ?? '—'} ok={(audit.bulk_sentinel_residue ?? 0) < 10} />
        </Section>

        <Section title="Finance Collections (row counts)" icon={<Database className="h-4 w-4 text-slate-600" />}>
          {Object.entries(finance).map(([k, v]) => (
            <Pill key={k} label={k} value={v} ok />
          ))}
        </Section>

        <Section title="CI Gate Baselines" icon={<ShieldAlert className="h-4 w-4 text-slate-600" />}>
          {Object.entries(gates).map(([k, v]) => (
            <Pill key={k} label={k} value={v} ok={v >= 0 && v < 500} />
          ))}
        </Section>

        <Section title="Recommended Next Step" icon={<CheckCircle2 className="h-4 w-4 text-slate-600" />}>
          <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="recommended-next-step">
            <div className="text-sm font-semibold text-slate-900">Step {next_step.step ?? '—'}: {next_step.label || '—'}</div>
            <div className="mt-1 text-xs text-slate-600">{next_step.action}</div>
          </div>
        </Section>
      </div>

      {/* Phase 3F.5 — Split-billing reconciliation drift tile (dev mode) */}
      <SplitBillingDriftTile />

      <div className="text-xs text-slate-500" data-testid="command-center-footer">
        Day 12 — Platform Readiness Aggregator. Auto-refreshes every 30 seconds. Read-only — no mutation actions.
      </div>
    </div>
  );
}
